export type ServiceSlug =
  | "lived-in-blondes"
  | "dimensional-color"
  | "brunettes"
  | "coppers"
  | "hair-extensions"
  | "precision-cutting";

export interface Service {
  slug: ServiceSlug;
  name: string;
  short: string;
  description: string;
  hero: { src: string; srcSet?: string; alt: string };
  philosophy: { title: string; body: string }[];
}

const img = (base: string) => ({
  src: `/assets/services/${base}.jpg`,
  srcSet: [
    `/assets/services/${base}.avif 1x`,
    `/assets/services/${base}.webp 1x`,
  ].join(", "),
  type: "image/jpeg" as const,
});

export const SERVICES: Service[] = [
  {
    slug: "lived-in-blondes",
    name: "Lived-in blondes",
    short: "Soft, natural grow out, no harsh regrowth lines.",
    description:
      "Hand-painted ribbons and root-melt toning for blonde that looks lived-in from day one and grows out gracefully.",
    hero: { ...img("09_golden_radiance_balayage_1x1"), alt: "Lived-in blonde with dimensional warmth" },
    philosophy: [
      {
        title: "Hand-Painted Placement",
        body: "Each ribbon is painted to follow how light naturally falls across the head, so the color reads dimensional at any angle.",
      },
      {
        title: "Root-Melt Toners",
        body: "A soft transition at the scalp lets you grow out without the banded two-tone line that plagues typical highlights.",
      },
      {
        title: "Gloss That Lasts",
        body: "Toning between full appointments keeps your blonde luminous, not brassy, so you stay warm between visits.",
      },
    ],
  },
  {
    slug: "dimensional-color",
    name: "Dimensional color",
    short: "Tone, depth, and placement considered together.",
    description:
      "Color built as a system — base tone, mid-shaft depth, and face-framing lightness — designed around how you actually wear your hair.",
    hero: { ...img("03_honey_blonde_dimensional_waves_1x1"), alt: "Dimensional warm blonde with light ribbons" },
    philosophy: [
      {
        title: "Three-Tone System",
        body: "A base, mid-shaft depth, and lighter face-frame are tuned together so the color moves with light, not against it.",
      },
      {
        title: "Skin-Tone Match",
        body: "Undertone is matched against your complexion, so the result flatters in any light — bright sun or warm interiors.",
      },
      {
        title: "Considered Refresh",
        body: "Each visit re-balances the system rather than starting over, so your hair stays healthier and the color stays coherent.",
      },
    ],
  },
  {
    slug: "brunettes",
    name: "Brunettes",
    short: "Rich, glossy warmth that flatters your skin tone.",
    description:
      "Layered brunettes that move with light — caramel, mocha, and espresso ribbons placed for richness and grow-out forgiveness.",
    hero: { ...img("02_caramel_brunette_glaze_1x1"), alt: "Rich brunette with caramel ribbons" },
    philosophy: [
      {
        title: "Tone-Matched Warmth",
        body: "Warmth is chosen against your skin tone, not a trend photo. Golden, neutral, or cool — the ribbon palette is built for you.",
      },
      {
        title: "Gloss & Glaze Finishes",
        body: "Translucent glazes seal in shine and deepen tone without commitment, keeping brunette rich between full color sessions.",
      },
      {
        title: "Grow-Out Friendly",
        body: "Dimensional placement means regrowth softens into the blend instead of drawing a hard line at the scalp.",
      },
    ],
  },
  {
    slug: "coppers",
    name: "Coppers",
    short: "From subtle shimmer to full-bodied rust.",
    description:
      "Copper and red color that stays luminous. Layered reds and coppers keep light moving through the hair at every angle.",
    hero: { ...img("01_copper_sunset_waves_1x1"), alt: "Copper sunset waves with dimensional warmth" },
    philosophy: [
      {
        title: "Undertone Selection",
        body: "Copper ranges from soft copper shimmer to deep rust. The undertone is picked against your skin tone and natural base so it glows instead of shouting.",
      },
      {
        title: "Vibrancy Plan",
        body: "Reds fade faster than any other family. You leave with a wash-and-care plan and a gloss cadence that keeps the tone saturated between visits.",
      },
      {
        title: "Placement With Intention",
        body: "Brightness is placed where light hits — the face frame, the crown, the ends — so the color reads dimensional, not dyed.",
      },
    ],
  },
  {
    slug: "hair-extensions",
    name: "Extensions",
    short: "Discreet, wearable length, matched to you.",
    description:
      "Hand-tied extensions matched to your texture and color. Discreet additions that move naturally and last between installs.",
    hero: { ...img("08_champagne_blonde_glamour_1x1"), alt: "Champagne blonde with seamless length" },
    philosophy: [
      {
        title: "Texture Match",
        body: "Donor hair is selected for curl, density, and behavior so the additions blend even when you wear your hair natural and air-dried.",
      },
      {
        title: "Color Calibration",
        body: "Tone is matched under multiple light sources — daylight, indoor warm, indoor cool — so the seam disappears in every setting.",
      },
      {
        title: "Wearable Length",
        body: "Length and weight are tuned to your styling routine. Real wearability beats Instagram drama every time.",
      },
    ],
  },
  {
    slug: "precision-cutting",
    name: "Precision cutting",
    short: "Considered cuts that hold their shape.",
    description:
      "Cuts built around growth pattern, density, and how you actually style in the morning. Shape is engineered, not guessed.",
    hero: { ...img("07_modern_textured_lob_1x1"), alt: "Precision lob with modern texture" },
    philosophy: [
      {
        title: "Growth-Pattern Mapping",
        body: "Cuts account for cowlicks, density changes, and how your hair falls naturally, so the shape gets better as it grows out.",
      },
      {
        title: "Dry-Cut Refinement",
        body: "Final detailing is done dry where the hair lives, so what you see in the mirror is the shape you get at home.",
      },
      {
        title: "Style-Realistic Length",
        body: "Every cut is checked against your real styling routine. If you will not use a round brush, the cut will not require one.",
      },
    ],
  },
];

if (SERVICES.length !== 6) {
  throw new Error(`expected 6 services, got ${SERVICES.length}`);
}

